/**
 * A utility for cross-application communication using window.postMessage.
 */
class CrossAppCommunicator {
  /**
   * @param {string} appName - A label for debugging (e.g., "ParentApp" or "ChildApp")
   * @param {string} targetOrigin - The expected origin of the other app (e.g., "https://other-app.web.app")
   */
  constructor(appName, targetOrigin) {
    this.appName = appName;
    this.targetOrigin = targetOrigin;
    this.messageHandlers = new Map();

    // Use the same bound reference so we can remove it later
    this.boundHandleMessage = this.handleMessage.bind(this);
    this.initListener();
  }

  /**
   * Attaches the message event listener.
   */
  initListener() {
    window.addEventListener("message", this.boundHandleMessage);
    console.log(`[${this.appName}] Message listener initialized for ${this.targetOrigin}.`);
  }

  /**
   * Handles incoming messages from other apps.
   * @param {MessageEvent} event
   */
  handleMessage(event) {
    if (event.origin !== this.targetOrigin) {
      console.warn(`[${this.appName}] Ignored message from unknown origin: ${event.origin}`);
      return;
    }

    const { type, payload } = event.data;

    console.log(`[${this.appName}] Received message:`, type, payload);

    const handler = this.messageHandlers.get(type);
    if (handler) {
      handler(payload, event);
    } else {
      console.warn(`[${this.appName}] No handler for message type: "${type}"`);
    }
  }

  /**
   * Registers a message handler.
   * @param {string} type - The message type.
   * @param {function} handler - Function to call with (payload, event).
   */
  on(type, handler) {
    if (typeof handler !== "function") {
      console.error(`[${this.appName}] Handler for "${type}" must be a function.`);
      return;
    }

    this.messageHandlers.set(type, handler);
    console.log(`[${this.appName}] Registered handler for type: "${type}"`);
  }

  /**
   * Sends a message to a target window.
   * @param {Window} targetWindow - e.g., iframe.contentWindow or window.parent
   * @param {string} type - The message type.
   * @param {any} payload - The data to send.
   */
  sendMessage(targetWindow, type, payload) {
    if (!targetWindow || typeof targetWindow.postMessage !== "function") {
      console.warn(`[${this.appName}] Cannot send message. Invalid target window.`);
      return;
    }

    const message = { type, payload };
    targetWindow.postMessage(message, this.targetOrigin);

    console.log(`[${this.appName}] Sent message:`, type, payload, this.targetOrigin);
  }

  /**
   * Cleans up the event listener and handlers.
   */
  destroy() {
    window.removeEventListener("message", this.boundHandleMessage);
    this.messageHandlers.clear();
    console.log(`[${this.appName}] Message listener destroyed.`);
  }
}

export default CrossAppCommunicator;
